import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "purchased", label: "In Inventory" },
  { value: "ready", label: "Ready" },
  { value: "repairing", label: "In Repair" },
  { value: "sold", label: "Sold" },
];

const Input = ({ value, onChange, type = "number", className = "" }) => (
  <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    className={`w-full px-2 py-1.5 rounded-md bg-secondary/60 border border-transparent focus:bg-card focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/30 focus:outline-none text-sm font-mono ${className}`} />
);

const Kpi = ({ label, value }) => (
  <Card className="p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
    <div className="font-display text-2xl md:text-3xl font-bold mt-1">{value}</div>
  </Card>
);

export default function BusinessSummary() {
  const [vehicles, setVehicles] = useState([]);
  const [sales, setSales] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    const [v, s] = await Promise.all([api.get("/vehicles"), api.get("/sales")]);
    setVehicles(v.data);
    setSales(Object.fromEntries(s.data.map((x) => [x.vehicle_id, x])));
  }, []);
  useEffect(() => { load(); }, [load]);

  const [quickSaleId, setQuickSaleId] = useState(null);
  const [quickSale, setQuickSale] = useState({ sale_price: "", sale_date: new Date().toISOString().slice(0,10), buyer_name: "", notes: "" });

  const submitQuickSale = async (vehicle) => {
    if (Number(quickSale.sale_price) <= 0) { toast.error("Enter a sale price"); return; }
    try {
      await api.post("/quick-sale", {
        vehicle_id: vehicle.id,
        sale_price: Number(quickSale.sale_price),
        sale_date: quickSale.sale_date,
        buyer_name: quickSale.buyer_name,
        notes: quickSale.notes,
      });
      toast.success(`Sold ${vehicle.year} ${vehicle.make} ${vehicle.model}`);
      setQuickSaleId(null);
      setQuickSale({ sale_price: "", sale_date: new Date().toISOString().slice(0,10), buyer_name: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sale failed"); }
  };

  const setDraft = (id, field, val) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: val } }));

  const saveRow = async (vehicle) => {
    const d = drafts[vehicle.id];
    if (!d) return;
    setSaving(vehicle.id);
    try {
      const patch = {};
      ["purchase_price", "tax_cost", "dealer_fee", "misc_costs"].forEach((k) => {
        if (d[k] !== undefined && d[k] !== "") patch[k] = Number(d[k]);
      });
      if (d.status) patch.status = d.status;
      if (d.source) patch.source = d.source;
      if (d.purchase_date) patch.purchase_date = d.purchase_date;
      await api.patch(`/vehicles/${vehicle.id}`, patch);
      toast.success("Saved");
      setDrafts((all) => { const n = { ...all }; delete n[vehicle.id]; return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  // Computed values per row using drafts or stored
  const enriched = vehicles.map((v) => {
    const d = drafts[v.id] || {};
    const purchase = Number(d.purchase_price ?? v.purchase_price ?? 0);
    const tax = Number(d.tax_cost ?? v.tax_cost ?? 0);
    const dealer = Number(d.dealer_fee ?? v.dealer_fee ?? 0);
    const repair = Number(d.misc_costs ?? v.misc_costs ?? 0);
    const total_investment = purchase + tax + dealer + repair;
    const sale = sales[v.id];
    const sale_price = sale?.sale_price || 0;
    const profit = sale ? sale_price - total_investment : 0;
    // Status logic per business rule (preserves manual 'repairing')
    let status = d.status ?? v.status;
    if (status !== "repairing") {
      status = sale_price > 0 ? "sold" : "purchased";
    }
    const source = d.source ?? v.source ?? v.auction_house ?? "other";
    return { v, d, purchase, tax, dealer, repair, total_investment, sale, sale_price, profit, status, source };
  });

  // Totals
  const totals = enriched.reduce((acc, r) => ({
    purchase: acc.purchase + r.purchase,
    tax: acc.tax + r.tax,
    dealer: acc.dealer + r.dealer,
    repair: acc.repair + r.repair,
    investment: acc.investment + r.total_investment,
    revenue: acc.revenue + r.sale_price,
    sold: acc.sold + (r.status === "sold" ? 1 : 0),
    inventory: acc.inventory + (r.status !== "sold" ? 1 : 0),
  }), { purchase: 0, tax: 0, dealer: 0, repair: 0, investment: 0, revenue: 0, sold: 0, inventory: 0 });

  const remainingInvestment = enriched
    .filter((r) => r.status !== "sold")
    .reduce((s, r) => s + r.total_investment, 0);

  return (
    <div data-testid="business-summary-page">
      <PageHeader
        overline="Business Summary"
        title="Spend & Inventory Overview"
        description="Live totals from your inventory. Edit any cell below to match your records — totals update instantly."
      />

      {/* 9 dashboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Kpi label="Vehicles Purchased" value={vehicles.length} />
        <Kpi label="Vehicles Sold" value={totals.sold} />
        <Kpi label="In Inventory" value={totals.inventory} />
        <Kpi label="Purchase Cost" value={fmtMoney(totals.purchase)} />
        <Kpi label="Taxes Paid" value={fmtMoney(totals.tax)} />
        <Kpi label="Dealer Fees" value={fmtMoney(totals.dealer)} />
        <Kpi label="Repair Costs" value={fmtMoney(totals.repair)} />
        <Kpi label="Total Investment" value={fmtMoney(totals.investment)} />
        <Kpi label="Sales Revenue" value={fmtMoney(totals.revenue)} />
      </div>

      <div className="mb-6">
        <Card className="p-5 border-blue-200 bg-blue-50/40 dark:bg-blue-500/5">
          <div className="text-xs uppercase tracking-wider text-blue-700 dark:text-blue-300 font-semibold">Remaining Inventory Investment</div>
          <div className="font-display text-3xl md:text-4xl font-bold mt-1 text-blue-700 dark:text-blue-300">{fmtMoney(remainingInvestment)}</div>
          <div className="text-xs text-muted-foreground mt-1">Cash currently tied up in unsold inventory ({totals.inventory} vehicles)</div>
        </Card>
      </div>

      {/* Data validation table */}
      <Card className="overflow-x-auto">
        <div className="p-5 border-b border-border">
          <h3 className="font-display text-xl font-semibold">Data Validation — All Vehicles</h3>
          <p className="text-sm text-muted-foreground mt-1">Click any field to edit. Status auto-flips to <b>Sold</b> when a sale exists, or you can manually mark <b>In Repair</b>.</p>
        </div>
        <table className="w-full text-sm min-w-[1400px]">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>{["Vehicle", "VIN", "Source", "Purchase Date", "Purchase", "Tax", "Dealer Fee", "Repair", "Total Investment", "Sale Price", "Sale Date", "Profit", "Status", "Save"].map((h) => <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {enriched.map(({ v, d, purchase, tax, dealer, repair, total_investment, sale, sale_price, profit, status, source }) => {
              const dirty = !!drafts[v.id];
              const profitColor = sale ? (profit >= 0 ? "text-emerald-600" : "text-red-600") : "text-muted-foreground";
              return (
                <React.Fragment key={v.id}>
                <tr className={`border-t border-border ${dirty ? "bg-blue-50/40 dark:bg-blue-500/5" : ""}`} data-testid={`validate-row-${v.id}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    <Link to={`/inventory/${v.id}`} className="hover:text-blue-600">{v.year} {v.make} {v.model} {v.trim}</Link>
                    <div className="text-[10px] text-muted-foreground font-mono">{v.stock_number}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{v.vin || "—"}</td>
                  <td className="px-3 py-2">
                    <select value={source} onChange={(e) => setDraft(v.id, "source", e.target.value)}
                      className="px-2 py-1.5 rounded-md bg-secondary/60 border border-transparent text-sm focus:bg-card focus:border-blue-500/40 focus:outline-none">
                      <option value="auction">Auction</option>
                      <option value="facebook">Facebook</option>
                      <option value="other">Other</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 w-36">
                    <Input type="date" value={(d.purchase_date ?? v.purchase_date ?? "").slice(0,10)} onChange={(val) => setDraft(v.id, "purchase_date", val)} />
                  </td>
                  <td className="px-3 py-2 w-28"><Input value={d.purchase_price ?? v.purchase_price ?? 0} onChange={(val) => setDraft(v.id, "purchase_price", val)} /></td>
                  <td className="px-3 py-2 w-24"><Input value={d.tax_cost ?? v.tax_cost ?? 0} onChange={(val) => setDraft(v.id, "tax_cost", val)} /></td>
                  <td className="px-3 py-2 w-24"><Input value={d.dealer_fee ?? v.dealer_fee ?? 0} onChange={(val) => setDraft(v.id, "dealer_fee", val)} /></td>
                  <td className="px-3 py-2 w-28"><Input value={d.misc_costs ?? v.misc_costs ?? 0} onChange={(val) => setDraft(v.id, "misc_costs", val)} /></td>
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{fmtMoney(total_investment)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{sale ? fmtMoney(sale_price) : "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{sale ? fmtDate(sale.sale_date || sale.created_at) : "—"}</td>
                  <td className={`px-3 py-2 font-semibold whitespace-nowrap ${profitColor}`}>{sale ? fmtMoney(profit) : "—"}</td>
                  <td className="px-3 py-2">
                    <select value={status} onChange={(e) => setDraft(v.id, "status", e.target.value)}
                      className="px-2 py-1.5 rounded-md bg-secondary/60 border border-transparent text-sm focus:bg-card focus:border-blue-500/40 focus:outline-none">
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {!sale && (
                      <button data-testid={`quick-sale-${v.id}`} onClick={() => setQuickSaleId(quickSaleId === v.id ? null : v.id)}
                        className="px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold whitespace-nowrap mr-1">
                        Mark Sold
                      </button>
                    )}
                    {dirty && (
                      <button data-testid={`save-${v.id}`} onClick={() => saveRow(v)} disabled={saving === v.id}
                        className="px-2 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                        {saving === v.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                      </button>
                    )}
                  </td>
                </tr>
                {quickSaleId === v.id && (
                  <tr className="bg-emerald-50/40 dark:bg-emerald-500/5">
                    <td colSpan={14} className="px-4 py-4">
                      <div className="flex items-end gap-3 flex-wrap">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sale Price</div>
                          <Input type="number" value={quickSale.sale_price} onChange={(val) => setQuickSale({ ...quickSale, sale_price: val })} className="w-32" />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Sale Date</div>
                          <Input type="date" value={quickSale.sale_date} onChange={(val) => setQuickSale({ ...quickSale, sale_date: val })} className="w-40" />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Buyer Name (optional)</div>
                          <Input type="text" value={quickSale.buyer_name} onChange={(val) => setQuickSale({ ...quickSale, buyer_name: val })} className="w-48" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Notes (optional)</div>
                          <Input type="text" value={quickSale.notes} onChange={(val) => setQuickSale({ ...quickSale, notes: val })} />
                        </div>
                        <button data-testid={`confirm-sale-${v.id}`} onClick={() => submitQuickSale(v)}
                          className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                          Mark as Sold
                        </button>
                        <button onClick={() => setQuickSaleId(null)}
                          className="px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 text-sm font-semibold">
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
          {/* Totals row */}
          <tfoot className="bg-secondary font-semibold border-t-2 border-foreground">
            <tr>
              <td colSpan={4} className="px-3 py-3 text-right uppercase text-xs tracking-wider">TOTALS</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.purchase)}</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.tax)}</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.dealer)}</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.repair)}</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.investment)}</td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.revenue)}</td>
              <td></td>
              <td className="px-3 py-3 font-mono">{fmtMoney(totals.revenue - totals.investment)}</td>
              <td colSpan={2} className="px-3 py-3 text-xs uppercase tracking-wider">{totals.sold} sold · {totals.inventory} in inventory</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}
